import Direction from 'assets/images/direction.svg';
import tractor from 'assets/images/tractor.svg';
import trailerEmpty from 'assets/images/trailer-empty.svg';
import trailerFull from 'assets/images/trailer-full.svg';
import { format, parseISO } from 'date-fns';

import { useTranslation } from 'react-i18next';

import { TableRow, TableCell, Box, Typography } from '@mui/material';

import { BadgeStatus } from '@/components/BadgeStatus';
import { BADGE_STATUS_COLOR } from '@/components/BadgeStatus/types';
import { FEATURES_FLAG_KEYS } from '@/features/featuresFlag/apis/featureFlag.keys';
import { useFeaturesFlagState } from '@/features/featuresFlag/hooks';
import { FolderItem } from '@/features/folders/types';
import { LANGUAGES } from '@/features/shared/constants';
import {
  isCustomPass,
  isBorderCrossingRoRo,
  isBorderCrossingRoRoEmptyV2,
  isBorderPassEasyWithLeShuttleFreightFull,
  isBorderPassEasyWithLeShuttleFreightEmpty,
} from '@/features/shared/helpers/typologies';

import {
  stylesVehicleBox,
  stylesFolderNumber,
  stylesVehicleIcons,
  stylesVehicleTrailor,
  stylesVehicleTractor,
  stylesFolderLineTable,
  stylesFolderLineTypology,
  stylesFolderLineCustomer,
  stylesFolderLineSpsBadge,
  stylesFolderLineTableLate,
  stylesFolderLineDirection,
  stylesFolderLineDirectionImg,
  stylesFolderLineDirectionInfo,
  stylesFolderLineDirectionArrival,
  stylesFolderLineDirectionDeparture,
  stylesFolderBookingTransportReference,
  stylesFolderLineInternalReferenceText,
} from './FolderLine.styles';
import { useFolderLine } from './hooks/useFolderLine';

export interface IFolderLineProps {
  folder: FolderItem;
  folderStatusLabel: string;
  folderStatusColor: BADGE_STATUS_COLOR;
}

export const FolderLine: React.FunctionComponent<IFolderLineProps> = ({
  folder,
  folderStatusLabel,
  folderStatusColor,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('folders');
  const { featuresFlag } = useFeaturesFlagState();

  const {
    folderId,
    trailerVrn,
    tractorVrn,
    folderNumber,
    crossingDate,
    folderTypologyId,
    requiresSpsFormalities,
    internalClientReference,
    bookingTransportReference,
  } = folder;

  const { isTodayIsTheDayBeforeCrossingDate, departure, arrival, handleNavigateToFolderDetail } = useFolderLine({
    folder,
    featuresFlag,
  });

  const isBPE = featuresFlag.get(FEATURES_FLAG_KEYS.BPE);
  const isSherpass = featuresFlag.get(FEATURES_FLAG_KEYS.SHERPASS);

  let selectedService = '';

  if (isSherpass && isCustomPass({ folderTypologyId })) {
    selectedService = t('folders.typology.BORDER_PASS_EASY_WITH_LE_SHUTTLE_FREIGHT.CUSTOMS_PASS');
  } else if (
    isSherpass &&
    (isBorderCrossingRoRo({ folderTypologyId }) || isBorderCrossingRoRoEmptyV2({ folderTypologyId }))
  ) {
    selectedService = t(
      'folders.typology.BORDER_PASS_EASY_WITH_LE_SHUTTLE_FREIGHT.BORDER_CROSSING_RORO.CUSTOMSPRO_ENS',
    );
  } else if (
    isBPE &&
    (isBorderCrossingRoRo({ folderTypologyId }) || isBorderCrossingRoRoEmptyV2({ folderTypologyId }))
  ) {
    selectedService = t(
      'folders.typology.BORDER_PASS_EASY_WITH_LE_SHUTTLE_FREIGHT.BORDER_CROSSING_RORO.BORDER_PASS_EASY',
    );
  } else if (isBPE) {
    selectedService = t(
      'folders.typology.BORDER_PASS_EASY_WITH_LE_SHUTTLE_FREIGHT.BORDER_CROSSING_RORO.BORDER_PASS_EASY',
    );
  }

  let trailerLoadModeSvg = '';

  if (
    isCustomPass({ folderTypologyId }) ||
    isBorderCrossingRoRo({ folderTypologyId }) ||
    isBorderPassEasyWithLeShuttleFreightFull({ folderTypologyId })
  ) {
    trailerLoadModeSvg = trailerFull;
  } else if (
    isBorderPassEasyWithLeShuttleFreightEmpty({ folderTypologyId }) ||
    isBorderCrossingRoRoEmptyV2({ folderTypologyId })
  ) {
    trailerLoadModeSvg = trailerEmpty;
  }

  return (
    <TableRow
      key={folderId}
      sx={isTodayIsTheDayBeforeCrossingDate ? stylesFolderLineTableLate : stylesFolderLineTable}
      onClick={handleNavigateToFolderDetail}
      data-testid='folderLine'
    >
      <TableCell>
        <Box sx={stylesFolderLineDirection}>
          <Box component='img' alt='Sharepass' src={Direction} sx={stylesFolderLineDirectionImg} />
          <Box sx={stylesFolderLineDirectionInfo}>
            <Typography variant='subtitle2' sx={stylesFolderLineDirectionDeparture}>
              {departure}
            </Typography>
            <Typography variant='caption' sx={stylesFolderLineDirectionArrival}>
              {arrival}
            </Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell sx={stylesFolderLineCustomer}>
        <Box sx={stylesFolderLineTypology}>{selectedService}</Box>
      </TableCell>
      <TableCell>
        <Typography variant='bodySM'>
          {crossingDate && format(parseISO(crossingDate), language === LANGUAGES.fr ? 'dd-MM-yyyy' : 'yyyy-MM-dd')}
        </Typography>
      </TableCell>
      <TableCell sx={stylesFolderLineCustomer}>
        <Box sx={stylesFolderLineInternalReferenceText}>{internalClientReference}</Box>
        <Box sx={stylesFolderNumber}>{folderNumber}</Box>
      </TableCell>
      <TableCell>
        <Box sx={{ ...stylesVehicleBox, mb: 2 }}>
          <Box component='img' alt='tractorVrn' sx={stylesVehicleIcons} src={tractor} />
          <Box sx={stylesVehicleTractor}>{tractorVrn}</Box>
        </Box>
        <Box sx={stylesVehicleBox}>
          <Box component='img' alt='trailerVrn' sx={stylesVehicleIcons} src={trailerLoadModeSvg} />
          <Box sx={stylesVehicleTrailor}>{trailerVrn ?? '-'}</Box>
        </Box>
      </TableCell>
      {isBPE && (
        <TableCell>
          <Box sx={stylesFolderBookingTransportReference}>#{bookingTransportReference}</Box>
        </TableCell>
      )}
      <TableCell>
        <BadgeStatus label={folderStatusLabel} statusColor={folderStatusColor} />
      </TableCell>
      <TableCell>{requiresSpsFormalities && <Box sx={stylesFolderLineSpsBadge}>SPS</Box>}</TableCell>
    </TableRow>
  );
};
